import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Hourglass, Pause, Trash2 } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { formatUsd } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { NoCargo } from '../components/NoCargo';
import { useFallo } from '../lib/useFallo';
import { useAuth, hasRole } from '../lib/auth';
import { useCheckPreview, CheckPreviewRow } from '../components/CheckPreview';
import { useScope, ScopeTabs } from '../lib/scope';
import { useClaimDrawer, ClientNameButton } from '../components/ClaimDrawer';
import { RChip } from '../components/ui/RChip';

function bankLabel(status: string): string {
  if (status === 'deposited_bofa') return 'BOFA';
  if (status === 'deposited_chase') return 'Chase';
  return '—';
}
function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

type WaitingCheck = {
  claim_team?: string | null;
  id: string;
  amount_cents: string | number;
  status: string;
  deposited_at: string | null;
  claim_id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  insurance_company: string | null;
  assigned_person: string | null;
};

/** Waiting to Clear: cheques depositados, con banco y días, y botón "Available in bank". */
export function WaitingToClear() {
  const { t } = useI18n();
  const { me } = useAuth();
  const [rows, setRows] = useState<WaitingCheck[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { fallo, marcar, limpiar } = useFallo();
  async function load() {
    // Una cola de cheques vacía por error se ve igual que una cola al día: nadie la trabaja.
    try { setRows((await api.get<{ checks: WaitingCheck[] }>('/api/checks/waiting-clear')).checks); limpiar(); }
    catch (e) { marcar(e); }
  }
  useEffect(() => { load(); }, []);

  async function checkAction(id: string, path: string, ok: string) {
    setBusy(id);
    try {
      await api.post(`/api/checks/${id}/${path}`);
      setMsg(ok);
      setTimeout(() => setMsg(null), 2500);
      await load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Bloqueado: ${e.message}` : 'Error.');
    } finally {
      setBusy(null);
    }
  }
  const markAvailable = (id: string) => checkAction(id, 'clear', 'OK');
  const bounce = (id: string) => checkAction(id, 'bounce', 'bounced');

  async function delCheck(id: string) {
    if (!window.confirm(t('cd.checkDeleteConfirm'))) return;
    setBusy(id);
    try {
      await api.del(`/api/checks/${id}`);
      setMsg('borrado');
      setTimeout(() => setMsg(null), 2500);
      await load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Error: ${e.message}` : 'Error.');
    } finally {
      setBusy(null);
    }
  }

  const q = search.trim().toLowerCase();
  // El buscador entra al scope: si no, los botones Mía/All cuentan la cola entera mientras la
  // lista muestra dos filas.
  const sc = useScope(rows, (r) => r.assigned_person, {
    getTeam: (r) => r.claim_team,
    fallbackToAll: true,
    filtro: (r) => !q || `${r.claim_number ?? ''} ${r.homeowner_name ?? ''} ${r.contractor_name ?? ''} ${r.insurance_company ?? ''}`.toLowerCase().includes(q),
  });
  const cd = useClaimDrawer({ float: true });
  const shown = sc.shown;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          {t('wtc.title')} {rows.length > 0 && <span className="badge gray">{shown.length}</span>}
        </h2>
        <span style={{ flex: 1 }} />
        <input className="input" style={{ maxWidth: 260 }} placeholder={t('claims.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <ScopeTabs {...sc} />{cd.drawer}
      {msg && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{msg}</div>}
      {fallo ? <NoCargo onRetry={load} /> : shown.length === 0 ? (
        <div className="muted">{t('wtc.empty')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('claims.num')}</th>
              <th>{t('q.insurance')}</th>
              <th>{t('claims.homeowner')}</th>
              <th>{t('cd.contractorOpt')}</th>
              <th>{t('wtc.bank')}</th>
              <th className="num">{t('common.amount')}</th>
              <th className="num">{t('wtc.days')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const d = daysSince(r.deposited_at);
              return (
                <tr key={r.id}>
                  <td>
                    <Link to={`/claims/${r.claim_id}`}>
                      <strong>{r.claim_number || `#${r.claim_id}`}</strong>
                    </Link>
                  </td>
                  <td>{r.insurance_company || '—'}</td>
                  <td><ClientNameButton claimId={r.claim_id} name={r.homeowner_name || '—'} onOpen={cd.open} bold={false} /></td>
                  <td>{r.contractor_name || '—'}</td>
                  <td><span className="badge gray">{bankLabel(r.status)}</span></td>
                  <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(r.amount_cents)}</td>
                  <td className="num">{d == null ? '—' : `${d}d`}</td>
                  <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button className="btn sm" disabled={busy === r.id} onClick={() => markAvailable(r.id)}>
                      {busy === r.id ? '…' : t('wtc.available')}
                    </button>
                    <button className="btn ghost sm" style={{ color: 'var(--orange)' }} disabled={busy === r.id} onClick={() => bounce(r.id)}>
                      {t('cd.bounce')}
                    </button>
                    {hasRole(me, 'super-manager') && (
                      <button className="btn ghost sm" style={{ color: 'var(--red)' }} disabled={busy === r.id} onClick={() => delCheck(r.id)}><Trash2 size={14} strokeWidth={1.75} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

type ReadyPayment = {
  claim_team?: string | null;
  payment_id: string;
  amount_cents: string | number;
  payee_type: 'homeowner' | 'contractor';
  authorized_at: string;
  claim_id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  check_status: string;
  bank: string | null;
  invoice_id: string | null;
  invoice_number: number | null;
  check_ready: boolean;
  auth_ready: boolean;
  pay_ready: boolean;
  payment_hold: boolean;
  payment_hold_note: string | null;
  assigned_person: string | null;
};

/**
 * Ready to Pay: pagos YA autorizados esperando que el cheque limpie. Cuando
 * alguien marca el cheque "available" (en Waiting to Clear), el pago pasa solo a
 * la Pay Queue. Muestra los días desde que se autorizó.
 */
type DeniedItem = {
  paymentId: string;
  claimId: string;
  invoiceId: string | null;
  invoiceNumber: number | null;
  amountCents: number;
  homeownerName: string | null;
  contractorName: string | null;
  claimNumber: string | null;
};

export function ReadyToPay() {
  const { t } = useI18n();
  const [rows, setRows] = useState<ReadyPayment[]>([]);
  const [denied, setDenied] = useState<DeniedItem[]>([]);
  const [tab, setTab] = useState<'waiting' | 'hold' | 'denied'>('waiting');
  const [search, setSearch] = useState('');
  const [openInv, setOpenInv] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const { fallo, marcar, limpiar } = useFallo();
  async function load() {
    try { setRows((await api.get<{ checks: ReadyPayment[] }>('/api/checks/ready-to-pay')).checks); limpiar(); }
    catch (e) { marcar(e); }
    try { setDenied((await api.get<{ items: DeniedItem[] }>('/api/payments/denied-auth')).items); } catch { /* ignore */ }
  }
  useEffect(() => { load(); }, []);

  // HOLD PAYMENT (#7): poner/quitar el hold del pago de un claim con una nota.
  async function toggleHold(r: ReadyPayment) {
    setMsg(null);
    try {
      if (r.payment_hold) {
        await api.del(`/api/claims/${r.claim_id}/payment-hold`);
        setMsg(t('hold.cleared'));
      } else {
        const note = window.prompt(t('hold.askNote'));
        if (note == null || !note.trim()) return;
        await api.post(`/api/claims/${r.claim_id}/payment-hold`, { note: note.trim() });
        setMsg(t('hold.set'));
      }
      await load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Error: ${e.message}` : 'Error.');
    }
  }
  // ⚠️ `heldRows` se define abajo, junto a `sc`/`q`: los contadores de las pestañas tienen que
  // salir de las filas ya buscadas y ya acotadas por el scope, no de la respuesta cruda.

  async function payHomeowner(it: DeniedItem) {
    setMsg(null);
    try {
      await api.post(`/api/payments/${it.paymentId}/pay-homeowner`);
      setMsg('Pago redirigido al homeowner.');
      await load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Error: ${e.message}` : 'Error.');
    }
  }

  const payeeName = (r: ReadyPayment) =>
    r.payee_type === 'contractor' ? (r.contractor_name || t('rtp.payCO')) : (r.homeowner_name || t('rtp.payHO'));

  const q = search.trim().toLowerCase();
  /* ALCANCE: scope (mine/all/member) + buscador. De acá salen la lista, los números de las
     pestañas Y los contadores del propio selector de persona. Antes `Waiting (N)` y `Hold (N)`
     contaban `rows` crudo: filtrabas por persona o escribías en el buscador, el badge del título
     bajaba y las pestañas de al lado seguían con el total — dos números distintos para la misma
     cosa, a diez píxeles uno del otro. */
  const sc = useScope(rows, (r) => r.assigned_person, {
    getTeam: (r) => r.claim_team,
    fallbackToAll: true,
    filtro: (r) => !q || `${r.claim_number ?? ''} ${r.homeowner_name ?? ''} ${r.contractor_name ?? ''}`.toLowerCase().includes(q),
  });
  const cd = useClaimDrawer({ float: true });
  const alcance = sc.shown;
  const shown = alcance;
  const heldRows = alcance.filter((r) => r.payment_hold);

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          {t('rtp.title')} {rows.length > 0 && <span className="badge gray">{shown.length}</span>}
        </h2>
        <span style={{ flex: 1 }} />
        <input className="input" style={{ maxWidth: 260 }} placeholder={t('claims.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button className={`btn sm${tab === 'waiting' ? '' : ' ghost'}`} onClick={() => setTab('waiting')}>{t('rtp.tabWaiting')}{alcance.length > 0 ? ` (${alcance.length})` : ''}</button>
        <button className={`btn sm${tab === 'hold' ? '' : ' ghost'}`} style={heldRows.length > 0 ? { color: tab === 'hold' ? undefined : 'var(--red)' } : undefined} onClick={() => setTab('hold')}>{t('hold.tab')}{heldRows.length > 0 ? ` (${heldRows.length})` : ''}</button>
        <button className={`btn sm${tab === 'denied' ? '' : ' ghost'}`} style={denied.length > 0 ? { color: tab === 'denied' ? undefined : 'var(--red)' } : undefined} onClick={() => setTab('denied')}>{t('rtp.tabDenied')}{denied.length > 0 ? ` (${denied.length})` : ''}</button>
      </div>
      {msg && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{msg}</div>}
      {tab === 'waiting' && (
      <>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 12 }}>{t('rtp.waitingHelp')}</div>
      <ScopeTabs {...sc} />{cd.drawer}
      {fallo ? <NoCargo onRetry={load} /> : shown.length === 0 ? (
        <div className="muted">{t('rtp.empty')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('claims.homeowner')}</th>
              <th>{t('rtp.payTo')}</th>
              <th>{t('rtp.invoices')}</th>
              <th>{t('wtc.bank')}</th>
              <th>{t('claims.colStatus')}</th>
              <th className="num">{t('common.amount')}</th>
              <th className="num">{t('wtc.days')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const d = daysSince(r.authorized_at);
              const isOpen = openInv === r.payment_id;
              return (
                <Fragment key={r.payment_id}>
                <tr style={r.payment_hold ? { background: 'var(--red-soft)' } : undefined}>
                  <td><ClientNameButton claimId={r.claim_id} name={r.homeowner_name || r.claim_number || `#${r.claim_id}`} onOpen={cd.open} /></td>
                  <td>{payeeName(r)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {r.invoice_id ? (
                        <button className="btn ghost sm" onClick={() => setOpenInv(isOpen ? null : r.payment_id)}>
                          {isOpen ? t('common.hide') : t('cd.viewPdf')}{r.invoice_number ? ` #${r.invoice_number}` : ''}
                        </button>
                      ) : <span className="muted">—</span>}
                      <button className="btn ghost sm" style={r.payment_hold ? { color: 'var(--red)' } : undefined} onClick={() => toggleHold(r)} title={r.payment_hold_note ?? ''}>
                        {r.payment_hold ? t('hold.release') : t('hold.action')}
                      </button>
                    </div>
                  </td>
                  <td>{r.bank || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
                      {/* Listo para pagar (cheque disponible + autorizado) y sin hold → link directo a
                          la Pay Queue, donde se sube el comprobante y se envía. Antes desaparecía de acá. */}
                      {r.pay_ready && !r.payment_hold ? (
                        <Link to="/pay-queue" className="btn sm" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' }}>
                          {t('rtp.goPay')}
                        </Link>
                      ) : (
                        <>
                          <RChip variant={r.check_ready ? 'ok' : 'warn'} icon={r.check_ready ? Check : Hourglass}>{t('rtp.confCheck')}</RChip>
                          {r.payee_type === 'contractor' && (
                            <RChip variant={r.auth_ready ? 'ok' : 'warn'} icon={r.auth_ready ? Check : Hourglass}>{t('rtp.confAuth')}</RChip>
                          )}
                          {r.payment_hold && (
                            <RChip variant="no" icon={Pause} title={r.payment_hold_note ?? ''}>{t('hold.badge')}</RChip>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(r.amount_cents)}</td>
                  <td className="num">{d == null ? '—' : `${d}d`}</td>
                </tr>
                {isOpen && r.invoice_id && (
                  <tr>
                    <td colSpan={7} style={{ background: 'var(--surface-2, #f8fafc)' }}>
                      <iframe title="invoice" src={`/api/invoices/${r.invoice_id}/pdf`} style={{ width: '100%', height: 560, border: '1px solid var(--border, #e5e7eb)', borderRadius: 8 }} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      </>
      )}
      {tab === 'hold' && (
      <>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 12 }}>{t('hold.help')}</div>
      {heldRows.length === 0 ? (
        <div className="muted">{t('hold.empty')}</div>
      ) : (
        <table className="tbl">
          <thead><tr>
            <th>{t('claims.homeowner')}</th>
            <th>{t('rtp.payTo')}</th>
            <th>{t('hold.note')}</th>
            <th className="num">{t('common.amount')}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {heldRows.map((r) => (
              <tr key={r.payment_id} style={{ background: 'var(--red-soft)' }}>
                <td><Link to={`/claims/${r.claim_id}`}><strong>{r.homeowner_name || r.claim_number || `#${r.claim_id}`}</strong></Link></td>
                <td>{payeeName(r)}</td>
                <td className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{r.payment_hold_note || '—'}</td>
                <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(r.amount_cents)}</td>
                <td className="num"><button className="btn ghost sm" onClick={() => toggleHold(r)}>{t('hold.release')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </>
      )}
      {tab === 'denied' && (
      <>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 12 }}>{t('rtp.deniedHelp')}</div>
      {denied.length === 0 ? (
        <div className="muted">{t('rtp.deniedEmpty')}</div>
      ) : (
        <table className="tbl">
          <thead><tr>
            <th>{t('claims.homeowner')}</th>
            <th>{t('cd.contractorOpt')}</th>
            <th>{t('rtp.invoices')}</th>
            <th className="num">{t('common.amount')}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {denied.map((it) => (
              <tr key={it.paymentId}>
                <td><Link to={`/claims/${it.claimId}`}><strong>{it.homeownerName || it.claimNumber || `#${it.claimId}`}</strong></Link></td>
                <td className="muted">{it.contractorName || '—'}</td>
                <td>{it.invoiceId ? <a className="btn ghost sm" href={`/api/invoices/${it.invoiceId}/pdf`} target="_blank" rel="noreferrer">{t('cd.viewPdf')}{it.invoiceNumber ? ` #${it.invoiceNumber}` : ''}</a> : <span className="muted">—</span>}</td>
                <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(it.amountCents)}</td>
                <td className="num"><button className="btn sm" onClick={() => payHomeowner(it)}>{t('rtp.payHo')} {it.homeownerName || ''}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </>
      )}
    </div>
  );
}

type BouncedCheck = {
  claim_team?: string | null;
  id: string;
  amount_cents: string | number;
  check_number: string | null;
  bounced_at: string | null;
  has_file: boolean;
  bank: string | null;
  claim_id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  insurance_company: string | null;
  assigned_person: string | null;
};

/** Bounced Checks: cheques rebotados, con foto y botón para pedir reissue (reinicia el conteo). */
export function BouncedChecks() {
  const { t } = useI18n();
  const [rows, setRows] = useState<BouncedCheck[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const { preview, toggle } = useCheckPreview();

  const { fallo, marcar, limpiar } = useFallo();
  async function load() {
    try { setRows((await api.get<{ checks: BouncedCheck[] }>('/api/checks/bounced')).checks); limpiar(); }
    catch (e) { marcar(e); }
  }
  useEffect(() => { load(); }, []);

  async function reissue(id: string) {
    setBusy(id);
    try {
      await api.post(`/api/checks/${id}/reissue`);
      setMsg(t('bnc.reissued'));
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      setMsg(e instanceof ApiError ? `Error: ${e.message}` : 'Error.');
    } finally {
      setBusy(null);
    }
  }

  const sc = useScope(rows, (r) => r.assigned_person, {
    getTeam: (r) => r.claim_team, fallbackToAll: true });
  const cd = useClaimDrawer({ float: true });

  return (
    <div className="card">
      <h2 className="card-title">
        {t('bnc.title')} {rows.length > 0 && <span className="badge red">{sc.shown.length}</span>}
      </h2>
      <ScopeTabs {...sc} />{cd.drawer}
      {msg && <div style={{ color: 'var(--green)', fontSize: 'var(--fs-sm)', marginBottom: 8 }}>{msg}</div>}
      {fallo ? <NoCargo onRetry={load} /> : sc.shown.length === 0 ? (
        <div className="muted">{t('bnc.empty')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('claims.num')}</th>
              <th>{t('q.insurance')}</th>
              <th>{t('claims.homeowner')}</th>
              <th>{t('cd.contractorOpt')}</th>
              <th>{t('wtc.bank')}</th>
              <th className="num">{t('common.amount')}</th>
              <th className="num">{t('wtc.days')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {sc.shown.map((r) => {
              const d = daysSince(r.bounced_at);
              return (
                <Fragment key={r.id}>
                <tr>
                  <td><Link to={`/claims/${r.claim_id}`}><strong>{r.claim_number || `#${r.claim_id}`}</strong></Link></td>
                  <td>{r.insurance_company || '—'}</td>
                  <td><ClientNameButton claimId={r.claim_id} name={r.homeowner_name || '—'} onOpen={cd.open} bold={false} /></td>
                  <td>{r.contractor_name || '—'}</td>
                  <td>{r.bank || '—'}</td>
                  <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(r.amount_cents)}</td>
                  <td className="num">{d == null ? '—' : `${d}d`}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    {r.has_file && <button className={`btn ghost sm${preview?.id === r.id ? ' active' : ''}`} onClick={() => toggle(r.id)}>{preview?.id === r.id ? t('chk.hidePhoto') : t('chk.viewPhoto')}</button>}
                    <button className="btn sm" disabled={busy === r.id} onClick={() => reissue(r.id)}>{t('bnc.reissue')}</button>
                  </td>
                </tr>
                {preview?.id === r.id && <CheckPreviewRow url={preview.url} colSpan={8} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

type HeldCheck = {
  claim_team?: string | null;
  id: string;
  amount_cents: string | number;
  holder: string;
  holder_since: string | null;
  check_number: string | null;
  claim_id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  insurance_company: string | null;
  assigned_person: string | null;
};

/**
 * Homeowner/Contractor with check: cheques que tiene en mano el HO o el
 * contratista (control manual). Muestra monto, días y un botón para marcar que
 * lo depositó (solo super-manager). Igual se puede facturar desde el claim.
 */
export function HeldChecks({ who }: { who: 'homeowner' | 'contractor' }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const [rows, setRows] = useState<HeldCheck[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { fallo, marcar, limpiar } = useFallo();
  async function load() {
    try { setRows((await api.get<{ checks: HeldCheck[] }>(`/api/checks/held/${who}`)).checks); limpiar(); }
    catch (e) { marcar(e); }
  }
  useEffect(() => { load(); }, [who]);

  async function markDeposited(r: HeldCheck) {
    setBusy(r.id);
    try {
      await api.post(`/api/checks/${r.id}/holder`, { holder: who === 'contractor' ? 'contractor_deposited' : 'homeowner_deposited' });
      await load();
    } catch (e) {
      setMsg(e instanceof ApiError ? `Bloqueado: ${e.message}` : 'Error.');
    } finally {
      setBusy(null);
    }
  }

  const q = search.trim().toLowerCase();
  const sc = useScope(rows, (r) => r.assigned_person, {
    getTeam: (r) => r.claim_team,
    fallbackToAll: true,
    filtro: (r) => !q || `${r.claim_number ?? ''} ${r.homeowner_name ?? ''} ${r.contractor_name ?? ''}`.toLowerCase().includes(q),
  });
  const cd = useClaimDrawer({ float: true });
  const shown = sc.shown;
  const title = who === 'contractor' ? t('held.contractorTitle') : t('held.homeownerTitle');

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          {title} {rows.length > 0 && <span className="badge gray">{shown.length}</span>}
        </h2>
        <span style={{ flex: 1 }} />
        <input className="input" style={{ maxWidth: 260 }} placeholder={t('claims.searchPh')} value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginBottom: 12 }}>{t('held.help')}</div>
      <ScopeTabs {...sc} />{cd.drawer}
      {fallo ? <NoCargo onRetry={load} /> : shown.length === 0 ? (
        <div className="muted">{t('held.empty')}</div>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t('claims.num')}</th>
              <th>{t('claims.homeowner')}</th>
              <th className="num">{t('common.amount')}</th>
              <th className="num">{t('wtc.days')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const d = daysSince(r.holder_since);
              return (
                <tr key={r.id}>
                  <td><Link to={`/claims/${r.claim_id}`}><strong>{r.claim_number || `#${r.claim_id}`}</strong></Link></td>
                  <td><ClientNameButton claimId={r.claim_id} name={r.homeowner_name || '—'} onOpen={cd.open} bold={false} />{who === 'contractor' && r.contractor_name ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.contractor_name}</div> : null}</td>
                  <td className="num" style={{ fontFamily: 'var(--font-mono)' }}>{formatUsd(r.amount_cents)}</td>
                  <td className="num">{d == null ? '—' : `${d}d`}</td>
                  <td>
                    {hasRole(me, 'super-manager') ? (
                      <button className="btn sm" disabled={busy === r.id} onClick={() => markDeposited(r)}>{t('held.markDeposited')}</button>
                    ) : <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {msg && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-sm)', marginTop: 8 }}>{msg}</div>}
    </div>
  );
}
